// features/dashboard/Dashboard.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import {
  fetchDashboardData,
  fetchLockoutDevices,
  fetchInvoices,
  checkPaymentStatus,
  createPaymentIntent,
  cancelPayment,
  processCashPayment,
  setPaymentDialogOpen,
  setCashDialogOpen,
  setTimeLeft,
  decrementTimeLeft,
  resetPaymentState,
} from '../../features/dashboard/dashboardSlice';
import './dashboard.css';
import moment from 'moment';
import { CircleCheck, TriangleAlert } from 'lucide-react';

// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);
const stripeKey = process.env.REACT_APP_STRIPE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;
 

// Payment Form Component
const PaymentForm = ({ clientSecret, onCancel, amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const user = JSON.parse(localStorage.getItem('admin_user') || '{}');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${process.env.REACT_APP_API_URL || 'https://alcobrakeadmin.com'}/v3/successdealertransaction?dealer=${user.dealerId}&email=${user.emailId}&paymentmode=online&paymentid=${clientSecret.split('_secret')[0]}`,
      },
    });

    if (error) {
      console.error('Payment error:', error);
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form-modern">
      <div className="payment-amount-display">
        <span className="amount-label">Payment Amount</span>
        <span className="amount-value">${amount.toFixed(2)}</span>
      </div>
      <PaymentElement />
      <div className="payment-actions-modern">
        <button type="button" onClick={onCancel} className="btn-cancel-modern">Cancel</button>
        <button type="submit" disabled={!stripe || isProcessing} className="btn-pay-modern">
          {isProcessing ? 'Processing...' : 'Pay Now'}
        </button>
      </div>
    </form>
  );
};

// Stat Card Component
const StatCard = ({ icon, title, value, color, subValue, loading }) => (
  <div className="stat-card" style={{ '--card-color': color }}>
    <div className="stat-card-icon">{icon}</div>
    <div className="stat-card-content">
      <div className="stat-card-title">{title}</div>
      <div className="stat-card-value">
        {loading ? <span className="loading-skeleton">---</span> : value}
      </div>
      {subValue && !loading && <div className="stat-card-sub">{subValue}</div>}
    </div>
  </div>
);

// Feature Card Component
const FeatureCard = ({ icon, title, description }) => (
  <div className="feature-card-modern">
    <div className="feature-icon">{icon}</div>
    <h3 className="feature-title">{title}</h3>
    <p className="feature-description">{description}</p>
  </div>
);

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, itemsPerPage, onItemsPerPageChange, totalItems }) => {
  const [goToPage, setGoToPage] = useState('');
  const pageNumbers = [];
  const maxVisiblePages = 5;
  
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }
  
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }
  
  return (
    <div className="dashboard-lockout-pagination">
      <div className="dashboard-lockout-pagination-info">
        Showing <strong>{totalItems ? ((currentPage - 1) * itemsPerPage) + 1 : 0}–{Math.min(currentPage * itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong>
      </div>
      <div className="dashboard-lockout-pagination-controls">
        <button onClick={() => onPageChange(1)} disabled={currentPage === 1} className="dashboard-lockout-page-btn">&laquo;</button>
        <button 
          onClick={() => onPageChange(currentPage - 1)} 
          disabled={currentPage === 1}
          className="dashboard-lockout-page-btn"
        >
          ‹ Prev
        </button>
        
        {startPage > 1 && (
          <>
            <button onClick={() => onPageChange(1)} className="dashboard-lockout-page-btn">1</button>
            {startPage > 2 && <span className="dashboard-lockout-page-dots">•••</span>}
          </>
        )}
        
        {pageNumbers.map(number => (
          <button
            key={number}
            onClick={() => onPageChange(number)}
            className={`dashboard-lockout-page-btn ${currentPage === number ? 'active' : ''}`}
          >
            {number}
          </button>
        ))}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="dashboard-lockout-page-dots">•••</span>}
            <button onClick={() => onPageChange(totalPages)} className="dashboard-lockout-page-btn">{totalPages}</button>
          </>
        )}
        
        <button 
          onClick={() => onPageChange(currentPage + 1)} 
          disabled={currentPage === totalPages}
          className="dashboard-lockout-page-btn"
        >
          Next ›
        </button>
        {/* Go to last page */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="dashboard-lockout-page-btn"
          aria-label="Last page"
        >
          »
        </button>
      </div>
      
      <div className="dashboard-lockout-per-page">
        <span>Show:</span>
        <select 
          value={itemsPerPage} 
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="dashboard-lockout-per-page-select"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
        </select>
      </div>
      <div className="dashboard-lockout-goto">
        <span>Go to</span>
        <form onSubmit={(event) => {
          event.preventDefault();
          const page = Number(goToPage);
          if (Number.isInteger(page) && page >= 1 && page <= totalPages) onPageChange(page);
          setGoToPage('');
        }}>
          <input type="number" min="1" max={totalPages} value={goToPage} onChange={(event) => setGoToPage(event.target.value)} placeholder="–" />
          <button type="submit" aria-label="Go to page">↵</button>
        </form>
      </div>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const dispatch = useDispatch();
  const { metrics, charts, tables, paymentStatus, invoices, paymentIntent, loading, paymentDialogOpen, cashDialogOpen, timeLeft } = 
    useSelector((state) => state.dashboard);
  
  const [searchLockout, setSearchLockout] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [paymentTimer, setPaymentTimer] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [paymentChecked, setPaymentChecked] = useState(false);
  const [featuresDialogOpen, setFeaturesDialogOpen] = useState(false);
  
  // Separate loading states for different data
  const [dashboardDataLoaded, setDashboardDataLoaded] = useState(false);
  const [lockoutDataLoaded, setLockoutDataLoaded] = useState(false);
  
  // Pagination states for lockouts
  const [lockoutPage, setLockoutPage] = useState(1);
  const [lockoutItemsPerPage, setLockoutItemsPerPage] = useState(10);
  
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('admin_user') || '{}');
    } catch (e) {
      return {};
    }
  }, []);
  
  // Features data
  const features = [
    {
      icon: "📸",
      title: "First In Camera Technology",
      description: "Positive Photo Identification. A cloud based optional facial recognition system is a technology capable of identifying a person from a digital image whether the concerned person is taking BrAc test or not."
    },
    {
      icon: "📍",
      title: "GPS Tracking",
      description: "Real-time GPS tracking with location history and route playback. Monitor vehicle movements and get accurate location data at all times."
    },
    {
      icon: "📊",
      title: "Best in Class Reporting",
      description: "Comprehensive reporting system with detailed analytics. Generate custom reports for compliance, violations, and vehicle usage patterns."
    },
    {
      icon: "🤖",
      title: "AI Face Recognition",
      description: "Advanced facial recognition technology ensures the correct person is taking the breath test. Prevents circumvention and ensures compliance."
    }
  ];
  
  // Initialize Data
  useEffect(() => {
    const initData = async () => {
      if (!initialized) {
        setInitialized(true);
        await dispatch(checkPaymentStatus(user.customer_id));
        setPaymentChecked(true);
      }
    };
    initData();
  }, [dispatch, user.customer_id, initialized]);
  
  // Load dashboard data after payment status is checked
  useEffect(() => {
    const loadDashboardData = async () => {
      if (paymentChecked && paymentStatus.hasAccess && !paymentStatus.isPaymentDue) {
        await dispatch(fetchDashboardData(user));
        setDashboardDataLoaded(true);
        await dispatch(fetchLockoutDevices(user));
        setLockoutDataLoaded(true);
      }
      if (paymentChecked && paymentStatus.isPaymentDue) {
        await dispatch(fetchInvoices(user.dealerId));
        setDashboardDataLoaded(true);
        setLockoutDataLoaded(true);
      }
    };
    loadDashboardData();
  }, [dispatch, user, paymentChecked, paymentStatus.hasAccess, paymentStatus.isPaymentDue]);
  
  // Payment Timer Effect
  useEffect(() => {
    if (timeLeft > 0 && !paymentTimer) {
      const timer = setInterval(() => {
        dispatch(decrementTimeLeft());
      }, 1000);
      setPaymentTimer(timer);
    } else if (timeLeft === 0 && paymentTimer) {
      clearInterval(paymentTimer);
      setPaymentTimer(null);
      dispatch(resetPaymentState());
    }
    return () => {
      if (paymentTimer) clearInterval(paymentTimer);
    };
  }, [timeLeft, paymentTimer, dispatch]);
  
  const handlePayNow = async () => {
    const response = await dispatch(fetchInvoices(user.dealerId));
    if (response.payload && response.payload.length > 0) {
      const totalAmount = response.payload.reduce((sum, inv) => sum + parseFloat(inv.total), 0);
      await dispatch(createPaymentIntent({ dealerId: user.dealerId, amount: totalAmount }));
      dispatch(setTimeLeft(180));
    }
  };
  
  const handleCashPayment = async () => {
    if (!adminPassword) {
      alert('Please enter admin password');
      return;
    }
    await dispatch(processCashPayment({
      dealerId: user.dealerId,
      adminPassword,
      paymentIntentId: paymentIntent.intentId,
      userEmail: user.emailId,
    }));
    setAdminPassword('');
    window.location.reload();
  };
  
  const handleCancelPayment = () => {
    if (paymentIntent.intentId) {
      dispatch(cancelPayment(paymentIntent.intentId));
    }
    dispatch(setPaymentDialogOpen(false));
    dispatch(resetPaymentState());
  };
  
  // Filter and paginate lockouts
  const filteredLockouts = tables.lockouts && tables.lockouts.length > 0 && lockoutDataLoaded
    ? tables.lockouts.filter(lock =>
        (lock.dealerName || '').toLowerCase().includes(searchLockout.toLowerCase()) ||
        (lock.devId || '').toLowerCase().includes(searchLockout.toLowerCase())
      )
    : [];
  
  const totalLockoutPages = Math.max(1, Math.ceil(filteredLockouts.length / lockoutItemsPerPage));
  const paginatedLockouts = filteredLockouts.slice(
    (lockoutPage - 1) * lockoutItemsPerPage,
    lockoutPage * lockoutItemsPerPage
  );
  
  // Prepare chart data
  const salesChartData = charts.salesData && charts.salesData.length > 0 && dashboardDataLoaded
    ? charts.salesData 
    : [
        { name: 'Jan', revenue: 0 },
        { name: 'Feb', revenue: 0 },
        { name: 'Mar', revenue: 0 },
        { name: 'Apr', revenue: 0 },
        { name: 'May', revenue: 0 },
        { name: 'Jun', revenue: 0 },
      ];
  
  // Device distribution calculations
  const totalDevices = metrics.totalDevices || 0;
  const activeDevices = metrics.availableDevices || 0;
  const inactiveDevices = totalDevices - activeDevices;
  
  // Reset pagination when search changes
  useEffect(() => {
    setLockoutPage(1);
  }, [searchLockout]);

  useEffect(() => {
    if (lockoutPage > totalLockoutPages) setLockoutPage(totalLockoutPages);
  }, [lockoutPage, totalLockoutPages]);
  
  if (!initialized || (loading && !paymentChecked)) {
    return (
      <div className="loading-modern">
        <div className="loading-spinner">
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
        </div>
        <p>Loading dashboard...</p>
      </div>
    );
  }
  
  // Access denied state
  if (!paymentStatus.hasAccess && !paymentStatus.isPaymentDue && paymentChecked) {
    return (
      <div className="access-denied-modern">
        <div className="access-denied-card">
          <div className="access-denied-icon">🔒</div>
          <h2>{paymentStatus.header || 'Access Restricted'}</h2>
          <p>{paymentStatus.message || 'Please contact customer care for assistance.'}</p>
          <div className="contact-info-modern">
            <h4>Customer Support</h4>
            <p>📧 info@alcobrake.com</p>
            <p>📞 +1 844-425-2627</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Payment due state
  if (paymentStatus.isPaymentDue && paymentChecked) {
    const totalDue = invoices.reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0);
    
    return (
      <div className="payment-due-modern">
        <div className="payment-due-container">
          <div className="payment-due-header">
            <div className="payment-icon">💰</div>
            <h1>Payment Required</h1>
            <p>Please complete your payment to access the dashboard</p>
          </div>
          
          <div className="invoices-summary">
            <h3>Pending Invoices</h3>
            <div className="invoices-list">
              {invoices && invoices.length > 0 ? (
                invoices.map((invoice, idx) => (
                  <div key={idx} className="invoice-item">
                    <div className="invoice-info">
                      <span className="invoice-id">#{invoice.id}</span>
                      <span className="invoice-date">{moment(invoice.date).format('MMM DD, YYYY')}</span>
                    </div>
                    <div className="invoice-amount">${parseFloat(invoice.total).toFixed(2)}</div>
                  </div>
                ))
              ) : (
                <div className="no-invoices">No pending invoices</div>
              )}
            </div>
            <div className="total-amount">
              <span>Total Due:</span>
              <strong>${totalDue.toFixed(2)}</strong>
            </div>
          </div>
          
          <div className="payment-options">
            <button onClick={handlePayNow} className="btn-payment-online">
              <span>💳</span> Pay Online
            </button>
            <button onClick={() => dispatch(setCashDialogOpen(true))} className="btn-payment-cash">
              <span>💰</span> Pay Cash
            </button>
          </div>
          
          {timeLeft > 0 && (
            <div className="payment-timer-modern">
              <div className="timer-circle">
                <svg className="timer-svg" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="45" fill="none" stroke="#e0e0e0" strokeWidth="8"/>
                  <circle 
                    cx="50" cy="50" r="45" fill="none" 
                    stroke="#0066FF" strokeWidth="8"
                    strokeDasharray={2 * Math.PI * 45}
                    strokeDashoffset={2 * Math.PI * 45 * (1 - timeLeft / 180)}
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <div className="timer-text">{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</div>
              </div>
              <p>Time remaining to complete payment</p>
            </div>
          )}
        </div>
        
        {/* Cash Payment Modal */}
        {cashDialogOpen && (
          <div className="modal-overlay-modern" onClick={() => dispatch(setCashDialogOpen(false))}>
            <div className="modal-content-modern" onClick={e => e.stopPropagation()}>
              <div className="modal-header-modern">
                <h3>Authorize Cash Payment</h3>
                <button className="modal-close" onClick={() => dispatch(setCashDialogOpen(false))}>×</button>
              </div>
              <div className="modal-body-modern">
                <input
                  type="password"
                  placeholder="Enter Admin Password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="input-modern"
                  autoFocus
                />
              </div>
              <div className="modal-footer-modern">
                <button onClick={() => dispatch(setCashDialogOpen(false))} className="btn-secondary-modern">Cancel</button>
                <button onClick={handleCashPayment} className="btn-primary-modern">Confirm Payment</button>
              </div>
            </div>
          </div>
        )}
        
        {/* Stripe Payment Modal */}
        {paymentDialogOpen && paymentIntent.clientSecret && (
          <div className="modal-overlay-modern" onClick={handleCancelPayment}>
            <div className="modal-content-modern modal-payment" onClick={e => e.stopPropagation()}>
              <div className="modal-header-modern">
                <h3>Complete Payment</h3>
                <button className="modal-close" onClick={handleCancelPayment}>×</button>
              </div>
              <div className="modal-body-modern">
                <Elements stripe={stripePromise} options={{ clientSecret: paymentIntent.clientSecret }}>
                  <PaymentForm 
                    clientSecret={paymentIntent.clientSecret}
                    onCancel={handleCancelPayment}
                    amount={paymentIntent.amount}
                  />
                </Elements>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Main dashboard view
  return (
    <div className="dashboard-modern">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1>Dashboard</h1>
          {/* <p>Welcome back, {user.name || 'User'}!</p> */}
        </div>
        {/* <div className="header-right">
          <div className="date-display">
            {moment().format('dddd, MMMM DD, YYYY')}
          </div>
          <button className="features-btn" onClick={() => setFeaturesDialogOpen(true)}>
            <span>✨</span> View More
          </button>
          <div className="user-avatar">
            {user.name?.charAt(0) || 'U'}
          </div>
        </div> */}
      </div>
      
      {/* Tabs */}
      <div className="dashboard-tabs">
        <button className={`tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
          Overview
        </button>
        <button className={`tab ${activeTab === 'lockouts' ? 'active' : ''}`} onClick={() => setActiveTab('lockouts')}>
          Lockout Devices
        </button>
      </div>
      
      {/* Stats Grid */}
      <div className="stats-grid">
        <StatCard 
          icon="📱" title="Total Devices" value={totalDevices.toLocaleString()} 
          color="#0066FF" subValue={`${activeDevices} Available`}
          loading={!dashboardDataLoaded}
        />
        <StatCard 
          icon="💰" title="Revenue This Month" value={`$${(metrics.currentRevenue || 0).toLocaleString()}`} 
          color="#00D4AA" subValue={`Total: $${(metrics.totalRevenue || 0).toLocaleString()}`}
          loading={!dashboardDataLoaded}
        />
        <StatCard 
          icon="👥" title="New Clients (30d)" value={(metrics.newClients || 0).toLocaleString()} 
          color="#FF6B6B" subValue={`Total: ${(metrics.totalClients || 0).toLocaleString()}`}
          loading={!dashboardDataLoaded}
        />
        <StatCard 
          icon="⚙️" title="Installations" value={(metrics.newInstallations || 0).toLocaleString()} 
          color="#FFD93D" subValue={`${metrics.removalsThisMonth || 0} Removals`}
          loading={!dashboardDataLoaded}
        />
      </div>
      
      {/* Charts Section - Overview Tab */}
      {activeTab === 'overview' && (
        <div className="charts-section">
          <div className="chart-card chart-large">
            <div className="chart-header">
              <h3>Sales Report (Last 6 Months)</h3>
            </div>
            {!dashboardDataLoaded ? (
              <div className="chart-loading">
                <div className="loading-skeleton-chart"></div>
                <p>Loading chart data...</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={salesChartData}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0066FF" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#0066FF" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="var(--border-light)" strokeDasharray="3 3" />
                  <XAxis dataKey="name" stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)' }} />
                  <YAxis stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--bg-secondary)', 
                      borderColor: 'var(--border-light)', 
                      borderRadius: '8px',
                      color: 'var(--text-primary)' 
                    }} 
                    formatter={(value) => `$${value.toLocaleString()}`} 
                  />
                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#0066FF" fill="url(#revenueGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
          
          {/* Device Distribution - Horizontal Bar Chart */}
          <div className="chart-card distribution-card">
            <div className="chart-header">
              <h3>Device Distribution</h3>
            </div>
            {!dashboardDataLoaded ? (
              <div className="chart-loading">
                <div className="loading-skeleton-chart"></div>
                <p>Loading device data...</p>
              </div>
            ) : totalDevices === 0 ? (
              <div className="no-devices-message">
                <div className="no-data-icon">📱</div>
                <p>No devices found</p>
              </div>
            ) : (
              <div className="device-distribution-bar">
                <div className="distribution-header">
                  <div className="distribution-label">
                    <span className="label-dot active-dot"></span>
                    <span>Active Devices</span>
                    <span className="distribution-count">{activeDevices}</span>
                  </div>
                  <div className="distribution-label">
                    <span className="label-dot inactive-dot"></span>
                    <span>Inactive Devices</span>
                    <span className="distribution-count">{inactiveDevices}</span>
                  </div>
                  <div className="distribution-total">
                    Total: {totalDevices} devices
                  </div>
                </div>
                
                <div className="distribution-bar-container">
                  <div 
                    className="distribution-bar-active"
                    style={{ width: `${(activeDevices / totalDevices) * 100}%` }}
                  >
                  </div>
                  <div 
                    className="distribution-bar-inactive"
                    style={{ width: `${(inactiveDevices / totalDevices) * 100}%` }}
                  >
                  </div>
                  <span className="bar-percentage bar-percentage--active">
                    {((activeDevices / totalDevices) * 100).toFixed(1)}%
                  </span>
                  <span className="bar-percentage bar-percentage--inactive">
                    {((inactiveDevices / totalDevices) * 100).toFixed(1)}%
                  </span>
                </div>
                
                <div className="distribution-stats">
                  <div className="stat-detail active-detail">
                    <div className="detail-icon detail-icon--active"><CircleCheck size={28} /></div>
                    <div className="detail-info">
                      <span className="detail-label">Active Devices</span>
                      <div className="detail-value">
                        <strong>{activeDevices}</strong> devices
                      </div>
                      <div className="detail-percentage">
                        {((activeDevices / totalDevices) * 100).toFixed(1)}% of total
                      </div>
                    </div>
                  </div>
                  <div className="stat-detail inactive-detail">
                    <div className="detail-icon detail-icon--inactive"><TriangleAlert size={28} /></div>
                    <div className="detail-info">
                      <span className="detail-label">Inactive Devices</span>
                      <div className="detail-value">
                        <strong>{inactiveDevices}</strong> devices
                      </div>
                      <div className="detail-percentage">
                        {((inactiveDevices / totalDevices) * 100).toFixed(1)}% of total
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Lockout Devices Table */}
      {activeTab === 'lockouts' && (
        <div className="tables-section">
          <div className="data-card full-width">
            <div className="data-card-header">
              <h3>Lockout Devices</h3>
              <div className="search-wrapper">
                <input
                  type="text"
                  placeholder="Search ..."
                  value={searchLockout}
                  onChange={(e) => setSearchLockout(e.target.value)}
                  className="search-input-modern"
                />
              </div>
            </div>
            <div className="table-responsive">
              <table className="data-table-modern">
                <thead>
                  <tr>
                    <th>Dealer Name</th>
                    {/* <th>S.No</th> */}
                    <th>Device Id</th>
                    <th>Event</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {!lockoutDataLoaded ? (
                    <tr>
                      <td colSpan="5" className="no-data">
                        <div className="no-data-icon">⏳</div>
                        <p>Loading lockout data...</p>
                        <small>Please wait while we fetch the data</small>
                       </td>
                     </tr>
                  ) : paginatedLockouts.length > 0 ? (
                    paginatedLockouts.map((lock, idx) => {
                      const eventTime = moment(lock.rtime);
                      
                      return (
                        <tr key={idx}>
                          <td>{lock.dealerName || '-'}</td>
                          {/* <td>{(lockoutPage - 1) * lockoutItemsPerPage + idx + 1}</td> */}
                          <td><code className="device-id">{lock.devId || '-'}</code></td>
                          <td>
                            <span className={`event-badge ${(lock.typeofevent || 'na').toLowerCase().replace(' ', '-')}`}>
                              {lock.typeofevent || 'NA'}
                            </span>
                          </td>
                          <td>{eventTime.format('MM/DD/YYYY HH:mm')}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="no-data">
                        <div className="no-data-icon">🔍</div>
                        <p>No lockout events found</p>
                        <small>Try adjusting your search criteria</small>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {lockoutDataLoaded && filteredLockouts.length > 0 && (
              <Pagination
                currentPage={lockoutPage}
                totalPages={totalLockoutPages}
                onPageChange={(page) => {
                  if (page >= 1 && page <= totalLockoutPages) setLockoutPage(page);
                }}
                itemsPerPage={lockoutItemsPerPage}
                onItemsPerPageChange={(count) => { setLockoutItemsPerPage(count); setLockoutPage(1); }}
                totalItems={filteredLockouts.length}
              />
            )}
          </div>
        </div>
      )}
      
      {/* Features Modal */}
      {featuresDialogOpen && (
        <div className="modal-overlay-modern" onClick={() => setFeaturesDialogOpen(false)}>
          <div className="modal-content-modern features-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header-modern features-modal-header">
              <h2>
                <span className="features-icon">✨</span>
                Advanced Features
              </h2>
              <button className="modal-close" onClick={() => setFeaturesDialogOpen(false)}>×</button>
            </div>
            <div className="modal-body-modern features-modal-body">
              <div className="features-grid">
                {features.map((feature, index) => (
                  <FeatureCard
                    key={index}
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                  />
                ))}
              </div>
            </div>
            <div className="modal-footer-modern features-modal-footer">
              <button onClick={() => setFeaturesDialogOpen(false)} className="btn-primary-modern">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
